import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cloudApi from "../../services/cloudApi";
import analytics from "../../services/analytics";

const CheckoutProcessing = ({ plan, interval, source, planData }) => {
  const [error, setError] = useState(null);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [statusText, setStatusText] = useState("Setting up your checkout...");
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const createSession = async () => {
      try {
        const result = await cloudApi.createCheckoutSession(plan, interval, { source });

        if (cancelled) return;

        if (result.url) {
          analytics.trackCheckoutStarted({ plan: planData, interval, source, flow: "wizard" });
          setStatusText("Redirecting to secure payment...");
          setTimeout(() => {
            window.location.href = result.url;
          }, 400);
        } else {
          setError("Unable to create checkout session. Please try again.");
        }
      } catch (err) {
        if (cancelled) return;
        const alreadySubscribed = err.message?.includes("Already subscribed");
        analytics.trackCheckoutFailed({
          plan: planData,
          interval,
          flow: "wizard",
          reason: err.message,
          already_subscribed: Boolean(alreadySubscribed),
        });
        if (alreadySubscribed) {
          setAlreadySubscribed(true);
        } else {
          setError(err.message || "Something went wrong. Please try again.");
        }
      }
    };

    createSession();
    return () => { cancelled = true; };
  }, [plan, interval, attempt]);

  const handleRetry = () => {
    setError(null);
    setStatusText("Setting up your checkout...");
    setAttempt((a) => a + 1);
  };

  if (alreadySubscribed) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-5">
            <svg className="w-7 h-7 text-blue-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-zinc-100 mb-2">
            You already have a subscription
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed mb-6">
            To switch plans, use the subscription management portal from your dashboard.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 text-black font-bold text-sm rounded-xl hover:shadow-xl hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 transition-all duration-300 active:scale-[0.98] border border-blue-300/50"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-5">
            <svg className="w-7 h-7 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-zinc-100 mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed mb-6">
            {error}
          </p>
          <button
            onClick={handleRetry}
            className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 text-black font-bold text-sm rounded-xl hover:shadow-xl hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 transition-all duration-300 active:scale-[0.98] border border-blue-300/50"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 flex flex-col items-center text-center">
        {/* Spinner */}
        <div className="relative w-14 h-14 mb-6">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-20 blur-lg animate-pulse" style={{ animationDuration: "2s" }} />
          <div className="relative w-14 h-14 rounded-full border-2 border-gray-200 dark:border-zinc-700 flex items-center justify-center">
            <svg className="w-7 h-7 text-blue-500 dark:text-cyan-400 animate-spin" style={{ animationDuration: "1.2s" }} viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        </div>

        <h2 className="text-lg font-semibold text-gray-800 dark:text-zinc-100 mb-2">
          {statusText}
        </h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed">
          You'll be redirected to Stripe's secure checkout page in just a moment.
        </p>

        {/* Trust indicators */}
        <div className="mt-6 flex items-center gap-2 text-[11px] text-gray-400 dark:text-zinc-500">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>Secured by Stripe</span>
        </div>
      </div>
    </div>
  );
};

export default CheckoutProcessing;
